import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import "./LoadingState.css";

export interface LoadingStateProps {
  /**
   * The visual variant of the loading indicator
   */
  variant?: "spinner" | "skeleton";

  /**
   * Optional message to display under the loading indicator
   */
  message?: string;

  /**
   * Size of the loading indicator
   */
  size?: "sm" | "md" | "lg";

  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * Whether the loading state should take up the full container height
   */
  fullHeight?: boolean;

  /**
   * Test ID for testing
   */
  testId?: string;
}

/**
 * LoadingState component displays a loading indicator with optional message
 * Used to indicate that content is loading
 */
const LoadingState: React.FC<LoadingStateProps> = ({
  variant = "spinner",
  message,
  size = "md",
  className = "",
  fullHeight = false,
  testId = "loading-state",
}) => {
  const renderLoadingIndicator = () => {
    if (variant === "spinner") {
      return (
        <div className={`loading-spinner ${size}`} data-testid={`${testId}-spinner`}>
          <FontAwesomeIcon icon={faSpinner} spin />
        </div>
      );
    }

    // Render skeleton variant
    return (
      <div className={`loading-skeleton ${size}`} data-testid={`${testId}-skeleton`}>
        {[...Array(3)].map((_, index) => (
          <div
            key={index}
            className="skeleton-item"
            style={{ width: `${Math.max(50, 80 - index * 15)}%` }}
          />
        ))}
      </div>
    );
  };

  return (
    <div
      className={`loading-state ${variant} ${size} ${fullHeight ? "full-height" : ""} ${className}`}
      data-testid={testId}
    >
      {renderLoadingIndicator()}

      {message && (
        <div className="loading-message" data-testid={`${testId}-message`}>
          {message}
        </div>
      )}
    </div>
  );
};

export default LoadingState;
